import { useEffect, useRef, useState } from 'react';
import {
  Building2, ArrowLeft, AlertTriangle, Loader2, CheckCircle2, Mail,
} from 'lucide-react';
import {
  joinFleetByCode, acceptFleetInvite, requestMagicLink,
  type AcceptFleetInviteResult,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

/**
 * Phase 3b Item 2 — Join Fleet entry surface.
 *
 * One page handles four flows because the URL shape disambiguates:
 *
 *   /join-fleet                  →  Code-entry form. Authenticated
 *                                    drivers who tap the join row in
 *                                    the Account section land here.
 *
 *   /join-fleet?code=<8 chars>   →  Shareable-link / QR landing (the
 *                                    admin invite panel's join_url).
 *                                    Signed in: the code pre-fills the
 *                                    form and submits once, exactly as
 *                                    if the driver had typed it.
 *                                    Signed out: the code is stashed in
 *                                    sessionStorage (same vehicle the
 *                                    home-invite token uses to survive
 *                                    the auth trip, Google OAuth bounce
 *                                    included) and the page offers
 *                                    Create account / Sign in. Create
 *                                    account carries the code to
 *                                    /register?fleet_code= so the new
 *                                    account is created in the fleet's
 *                                    building; Sign in relies on the
 *                                    stash alone. App.tsx's post-auth
 *                                    effect drains the stash either way.
 *                                    A malformed code is ignored and the
 *                                    plain form renders.
 *
 *   /join-fleet?token=<jwt>       →  Email-invite landing. Calls
 *                                    accept-invite on mount and
 *                                    branches:
 *                                       pending_verification
 *                                                     → the account exists;
 *                                                       the join applies at
 *                                                       its next verified
 *                                                       sign-in. Offer to
 *                                                       email the sign-in
 *                                                       link (whose verify
 *                                                       announces the join)
 *                                       already_member→ success state,
 *                                                       refresh user,
 *                                                       auto-nav home
 *                                       needs_register→ redirect to
 *                                                       /register?email=…
 *                                                       &pendingToken=…
 *                                                       (the token rides the
 *                                                       register body, so
 *                                                       the new account is
 *                                                       created in the fleet)
 *
 * EMAIL_MISMATCH / token-expired errors render a generic
 * "ask your fleet admin" message; we don't reveal whether the
 * mismatch was email or audience or expiration, matching the
 * other auth-token error surfaces.
 */

/** sessionStorage slot for a `?code=` that arrived signed out. Read
 *  and cleared by App.tsx's post-auth drain (and by this page's own
 *  auto-submit, whichever runs first — both clear before they POST). */
export const FLEET_JOIN_CODE_STORAGE_KEY = 'evi_fleet_join_code';

/** The emailed fleet INVITE token, parked while a brand-new invitee goes to
 *  sign up, so a Google signup started from the login screen carries it
 *  through the OAuth round trip (the server joins the new account to the
 *  fleet at creation). sessionStorage: this tab only, gone with it. */
export const FLEET_INVITE_TOKEN_STORAGE_KEY = 'evi_fleet_invite_token';

/** The invite's jti, read from the token's payload (no verification - the
 *  server re-checks everything). Only the jti goes to /auth/google: that URL
 *  is logged, and the token itself is a bearer credential. */
export function inviteJtiFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { jti?: unknown };
    return typeof json.jti === 'string' && /^[A-Za-z0-9-]{8,64}$/.test(json.jti) ? json.jti : null;
  } catch {
    return null;
  }
}

/** Drop the parked invite once a signup or sign-in has carried it. */
export function clearStashedFleetInviteToken(): void {
  try { sessionStorage.removeItem(FLEET_INVITE_TOKEN_STORAGE_KEY); } catch { /* storage blocked */ }
}

export function readStashedFleetInviteToken(): string | null {
  try {
    const v = sessionStorage.getItem(FLEET_INVITE_TOKEN_STORAGE_KEY);
    return v && v.length >= 16 && v.length <= 4096 ? v : null;
  } catch {
    return null;
  }
}

/** 8 chars of RFC 4648 base32 (A–Z, 2–7) — the server's fleet invite
 *  alphabet. Building codes are 6 chars on a different alphabet, so a
 *  building link pasted here falls out as null rather than being sent
 *  to the wrong endpoint. */
const FLEET_CODE_RE = /^[A-Z2-7]{8}$/;

/** Uppercase + strip whitespace/hyphens (the same normalisation the
 *  server applies), then accept only a well-formed fleet code. */
export function normalizeFleetJoinCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.toUpperCase().replace(/[\s-]/g, '');
  return FLEET_CODE_RE.test(code) ? code : null;
}

interface JoinFleetPageProps {
  onNavigate: (path: string) => void;
}

type LandingState =
  | { phase: 'code-entry' }
  | { phase: 'signed-out' }
  | { phase: 'token-processing' }
  | { phase: 'token-success'; fleetName: string }
  | { phase: 'token-pending'; fleetName: string; email: string }
  | { phase: 'token-error'; message: string };

function readToken(): string | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  return sp.get('token');
}

function readCodeParam(): string | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  return normalizeFleetJoinCode(sp.get('code'));
}

export function JoinFleetPage({ onNavigate }: JoinFleetPageProps) {
  const { t } = useTranslation('fleet');
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const initialToken = readToken();
  // Read once: the URL is stable for the life of this page view, and a
  // re-read on every render would re-arm the auto-submit.
  const [initialCode] = useState(() => (initialToken ? null : readCodeParam()));
  const [state, setState] = useState<LandingState>(() => {
    if (initialToken) return { phase: 'token-processing' };
    if (initialCode && !isAuthed) return { phase: 'signed-out' };
    return { phase: 'code-entry' };
  });

  // Link landing while signed out — park the code so register / login /
  // OAuth all converge on App.tsx's drain without revisiting this page.
  // If the session appears while we're still mounted (a login that
  // routes back here), fall through to the form, which auto-submits.
  useEffect(() => {
    if (!initialCode) return;
    if (!isAuthed) {
      try {
        sessionStorage.setItem(FLEET_JOIN_CODE_STORAGE_KEY, initialCode);
      } catch {
        // Storage blocked (private mode) — the driver can still type the code after signing in.
      }
      return;
    }
    setState((cur) => (cur.phase === 'signed-out' ? { phase: 'code-entry' } : cur));
  }, [isAuthed, initialCode]);

  // Email-invite branch — fire accept-invite on mount.
  useEffect(() => {
    if (!initialToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await acceptFleetInvite(initialToken);
        if (cancelled) return;
        await handleAcceptResult(res, initialToken, onNavigate, refreshUser, setState);
      } catch (err) {
        if (cancelled) return;
        let message = t('error.expired');
        if (err instanceof Error && 'response' in err) {
          try {
            const body = await (err as { response: Response }).response.json();
            const code: string | undefined = body?.error?.code;
            if (code === 'EMAIL_MISMATCH') {
              message = t('error.emailMismatch');
            } else if (code === 'INVITE_EXPIRED' || code === 'INVITE_NOT_FOUND') {
              // A dead link: the localized "this invite has expired" copy.
              message = t('error.expired');
            } else if (body?.error?.message) {
              message = body.error.message;
            }
          } catch {
            // fall through to generic
          }
        }
        setState({ phase: 'token-error', message });
      }
    })();
    return () => { cancelled = true; };
    // `react-hooks/exhaustive-deps` rule isn't registered in the shared
    // eslint config; intentionally scoping this effect to initialToken
    // only so the token-error flow doesn't loop on state updates.
  }, [initialToken]);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--color-bg, #0a0a0a)',
        color: 'var(--color-tx)',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <button
        type="button"
        onClick={() => onNavigate(isAuthed ? '/profile' : '/login')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none',
          color: 'var(--color-tx-m)', cursor: 'pointer', fontSize: 13,
          marginBottom: 16, padding: 0, alignSelf: 'flex-start',
        }}
      >
        <ArrowLeft size={14} /> {t('back')}
      </button>

      <div
        style={{
          maxWidth: 440,
          width: '100%',
          marginInline: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 12%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--evi-accent, #2ECC71)',
          }}>
            <Building2 size={22} />
          </div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{t('title')}</h1>
        </div>

        {state.phase === 'token-processing' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-tx-m)' }}>
            <Loader2 size={16} className="spin" /> {t('verifying')}
          </div>
        )}

        {state.phase === 'token-success' && (
          <div style={{
            padding: '18px 20px',
            background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)',
            borderRadius: 12,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle2 size={18} style={{ color: 'var(--evi-accent, #2ECC71)' }} />
              <strong style={{ fontSize: 15 }}>
                {t('success.joined', { fleetName: state.fleetName })}
              </strong>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-tx-m)' }}>
              {t('success.body')}
            </p>
          </div>
        )}

        {state.phase === 'token-pending' && (
          <PendingVerificationBody fleetName={state.fleetName} email={state.email} />
        )}

        {state.phase === 'token-error' && (
          <div style={{
            padding: '16px 18px',
            background: 'color-mix(in srgb, var(--evi-warn) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--evi-warn) 35%, transparent)',
            borderRadius: 12,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
              color: 'var(--evi-warn)',
            }}>
              <AlertTriangle size={16} />
              <strong style={{ fontSize: 14 }}>{t('error.title')}</strong>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-tx)' }}>
              {state.message}
            </p>
          </div>
        )}

        {state.phase === 'signed-out' && (
          <SignedOutBody onNavigate={onNavigate} code={initialCode} />
        )}

        {state.phase === 'code-entry' && (
          <CodeEntryForm
            onNavigate={onNavigate}
            refreshUser={refreshUser}
            initialCode={initialCode}
            // Auto-submit only when the code came in on the URL AND the
            // driver is signed in — a typed code never self-submits.
            autoSubmit={Boolean(initialCode) && isAuthed}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Signed-out link landing. The code is already parked in
 * sessionStorage by the parent's effect; this just tells the driver
 * what will happen and routes to register / login. Same two-button
 * shape as HomeInvitePage's signed_out view.
 *
 * Create account ALSO puts the code on the /register URL: the stash
 * alone gets the driver joined only if their account is already in
 * the fleet's building, and a brand-new account would otherwise be
 * created in the default tenant and refused CROSS_TENANT_INVITE at
 * the drain. With ?fleet_code= the server creates the account in the
 * fleet's tenant first, then the drain joins it. Sign in stays plain
 * /login — an existing account keeps its building, and the existing
 * cross-tenant handling in App.tsx covers the mismatch case.
 */
function SignedOutBody({
  onNavigate,
  code,
}: {
  onNavigate: (path: string) => void;
  code: string | null;
}) {
  const { t } = useTranslation('fleet');
  const registerPath = code ? `/register?fleet_code=${encodeURIComponent(code)}` : '/register';
  return (
    <div style={{
      padding: '18px 20px',
      background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
      border: '1px solid var(--color-brd, var(--evi-border))',
      borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
        {t('signedOut.heading')}
      </p>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
        {t('signedOut.body')}
      </p>
      <button
        type="button"
        onClick={() => onNavigate(registerPath)}
        style={{
          padding: '12px 14px',
          background: 'var(--evi-accent, #2ECC71)',
          color: '#000',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          marginTop: 4,
        }}
      >
        {t('signedOut.createAccount')}
      </button>
      <button
        type="button"
        onClick={() => onNavigate('/login')}
        style={{
          padding: '12px 14px',
          background: 'transparent',
          color: 'var(--color-tx)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {t('signedOut.signIn')}
      </button>
    </div>
  );
}

/**
 * Email-invite landing for an account that already exists. The server
 * has recorded the join, but it applies only at a verified sign-in (the
 * emailed link) — never on a password sign-in or an already-open
 * session, signed in or not. One button asks for that link;
 * MagicLinkVerifyPage announces the join when the driver opens it.
 */
function PendingVerificationBody({ fleetName, email }: { fleetName: string; email: string }) {
  const { t } = useTranslation('fleet');
  const [send, setSend] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const sendLink = async (): Promise<void> => {
    setSend('sending');
    try {
      await requestMagicLink(email);
      setSend('sent');
    } catch {
      setSend('error');
    }
  };

  return (
    <div style={{
      padding: '18px 20px',
      background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
      border: '1px solid var(--color-brd, var(--evi-border))',
      borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CheckCircle2 size={18} style={{ color: 'var(--evi-accent, #2ECC71)', flexShrink: 0 }} />
        <strong style={{ fontSize: 15 }}>{t('pending.heading')}</strong>
      </div>
      {send === 'sent' ? (
        <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--color-tx)', lineHeight: 1.5 }}>
          {t('pending.sent', { email, fleetName })}
        </p>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
            {t('pending.body', { email, fleetName })}
          </p>
          {/* Said BEFORE the sign-in that joins: never a silent reset. */}
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
            {t('pending.security')}
          </p>
          {send === 'error' && (
            <p style={{
              margin: 0, fontSize: 12, color: 'var(--evi-warn)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <AlertTriangle size={12} /> {t('pending.sendFailed')}
            </p>
          )}
          <button
            type="button"
            onClick={() => void sendLink()}
            disabled={send === 'sending'}
            style={{
              padding: '12px 14px',
              background: 'var(--evi-accent, #2ECC71)',
              color: '#000',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: send === 'sending' ? 'not-allowed' : 'pointer',
              opacity: send === 'sending' ? 0.6 : 1,
              marginTop: 4,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Mail size={16} /> {send === 'sending' ? t('pending.sending') : t('pending.send')}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Code-entry sub-form. Mounted from the no-token branch of
 * JoinFleetPage. Single 8-char base32 input + a submit button +
 * a "I have an email invite" info card below. With `initialCode`
 * the field pre-fills; with `autoSubmit` it submits that code once
 * on mount through the same handler a tap would, so link and typed
 * joins share one code path (and one set of error messages).
 */
function CodeEntryForm({
  onNavigate,
  refreshUser,
  initialCode,
  autoSubmit,
}: {
  onNavigate: (path: string) => void;
  refreshUser: () => Promise<void>;
  initialCode: string | null;
  autoSubmit: boolean;
}) {
  const { t } = useTranslation('fleet');
  const [code, setCode] = useState(initialCode ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // One-shot guard: a failed auto-submit leaves the code in the field
  // for the driver to retry by hand, never re-fires on re-render.
  const autoFired = useRef(false);

  const submitCode = async (raw: string): Promise<void> => {
    setErr(null);
    if (!raw.trim()) {
      setErr(t('code.emptyError'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await joinFleetByCode(raw.trim());
      await refreshUser();
      toast(
        t(res.already_member ? 'code.alreadyThisFleet' : 'code.welcome', { fleetName: res.fleet_name }),
        { tone: 'accent' },
      );
      // Drop the ?code= from the entry we are leaving behind, so Back
      // from /map lands on the plain form and never auto-submits again.
      // App.navigate pushes the next entry; this rewrites the current one.
      try { window.history.replaceState({}, '', '/join-fleet'); } catch { /* ignore */ }
      onNavigate('/map');
    } catch (e) {
      let message = t('code.genericError');
      if (e instanceof Error && 'response' in e) {
        try {
          const body = await (e as { response: Response }).response.json();
          const code2: string | undefined = body?.error?.code;
          if (code2 === 'INVITE_CODE_INVALID') {
            message = t('code.invalidCode');
          } else if (code2 === 'DRIVER_ALREADY_IN_FLEET') {
            message = t('code.alreadyInFleet');
          } else if (code2 === 'CROSS_TENANT_INVITE') {
            message = t('code.crossTenant');
          } else if (body?.error?.message) {
            message = body.error.message;
          }
        } catch {
          // fall through
        }
      }
      setErr(message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!autoSubmit || !initialCode || autoFired.current) return;
    autoFired.current = true;
    // Clear the stash BEFORE posting so App.tsx's drain (a parent
    // effect, which runs after this one) finds nothing and the code is
    // sent exactly once.
    try {
      sessionStorage.removeItem(FLEET_JOIN_CODE_STORAGE_KEY);
    } catch {
      // Storage blocked — nothing was stashed either.
    }
    void submitCode(initialCode);
  }, [autoSubmit, initialCode]);

  return (
    <>
      <div style={{
        padding: '18px 20px',
        background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
        border: '1px solid var(--color-brd, var(--evi-border))',
        borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          {t('code.heading')}
        </p>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-tx-m)' }}>
          {t('code.hint')}
        </p>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="A7K2P9X4"
          maxLength={16}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          disabled={submitting}
          aria-label={t('code.aria')}
          style={{
            padding: '12px 14px',
            background: 'var(--color-bg, #0a0a0a)',
            color: 'var(--color-tx)',
            border: '1px solid var(--color-brd, var(--evi-border))',
            borderRadius: 8,
            fontSize: 18,
            // Inherited face + tabular digits (house rule: no mono font on
            // numbers); the letter-spacing does the "code" reading instead.
            fontFamily: 'inherit',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '4px',
            textAlign: 'center',
            outline: 'none',
            marginTop: 4,
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--evi-accent, #2ECC71)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--color-brd, var(--evi-border))'; }}
        />
        {err && (
          <p style={{
            margin: 0, fontSize: 12, color: 'var(--evi-warn)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <AlertTriangle size={12} /> {err}
          </p>
        )}
        <button
          type="button"
          onClick={() => void submitCode(code)}
          disabled={submitting || code.trim().length < 4}
          style={{
            padding: '12px 14px',
            background: 'var(--evi-accent, #2ECC71)',
            color: '#000',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: submitting || code.trim().length < 4 ? 'not-allowed' : 'pointer',
            opacity: submitting || code.trim().length < 4 ? 0.6 : 1,
            marginTop: 4,
          }}
        >
          {submitting ? t('code.submitting') : t('code.submit')}
        </button>
      </div>

      <div style={{
        padding: '14px 18px',
        background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
        border: '1px solid var(--color-brd, var(--evi-border))',
        borderRadius: 12,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <Mail size={16} style={{ color: 'var(--color-tx-m)', flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
            {t('emailInvite.heading')}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
            {t('emailInvite.body')}
          </p>
        </div>
      </div>
    </>
  );
}

/**
 * Map accept-invite outcome → page state. Drains the auth refresh
 * + post-join navigation so the parent's mount effect stays
 * tight.
 */
async function handleAcceptResult(
  res: AcceptFleetInviteResult,
  token: string,
  onNavigate: (path: string) => void,
  refreshUser: () => Promise<void>,
  setState: (s: LandingState) => void,
): Promise<void> {
  if ('needs_registration' in res) {
    // Parked so a Google signup (from the login screen) keeps the invite.
    try { sessionStorage.setItem(FLEET_INVITE_TOKEN_STORAGE_KEY, token); } catch { /* storage blocked */ }
    const params = new URLSearchParams({
      email: res.email,
      pendingToken: token,
    });
    onNavigate(`/register?${params.toString()}`);
    return;
  }
  // No fleet_id means not joined: pending_verification, or a needs_login
  // from a server that predates the verified-sign-in rule. Either way,
  // never claim a join.
  if ('pending_verification' in res || !('fleet_id' in res)) {
    setState({ phase: 'token-pending', fleetName: res.fleet_name, email: res.email });
    return;
  }
  // already_member — nothing left to join; carry on as a join. Refresh
  // only with a session: signed out, /auth/me's 401 would bounce
  // through the refresh interceptor to /login.
  if (useAuthStore.getState().isAuthenticated) await refreshUser();
  setState({ phase: 'token-success', fleetName: res.fleet_name });
  setTimeout(() => onNavigate('/map'), 2000);
}

/**
 * Post-auth follow-up for LoginPage / RegisterPage when the driver
 * arrived with an email-invite token (?pendingToken=). A password
 * sign-in never completes a join, so pending_verification goes back to
 * the /join-fleet landing, which says so and offers the emailed
 * sign-in link, rather than claiming a join. Resolves false when there
 * is nothing to act on (the caller carries on with its own routing);
 * rejects on an accept-invite error, which the caller swallows.
 */
export async function continueFleetInvite(
  token: string,
  onNavigate: (path: string) => void,
): Promise<boolean> {
  const res = await acceptFleetInvite(token);
  if ('needs_registration' in res) return false;
  if ('pending_verification' in res || !('fleet_id' in res)) {
    onNavigate(`/join-fleet?${new URLSearchParams({ token }).toString()}`);
    return true;
  }
  // already_member — refresh so fleet_id + current_mode hydrate on the
  // auth store before /map renders.
  await useAuthStore.getState().refreshUser();
  toast(i18n.t('fleet:code.alreadyThisFleet', { fleetName: res.fleet_name }), { tone: 'accent' });
  onNavigate('/map');
  return true;
}
